import type React from "react"
import { useEffect, useRef } from "react"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useMobileMoreMenu } from "./mobile-more-menu-context"
import { ThemePill } from "./theme-pill"

const MoreIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="currentColor"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<circle cx="3.5" cy="8" r="1.25" />
		<circle cx="8" cy="8" r="1.25" />
		<circle cx="12.5" cy="8" r="1.25" />
	</svg>
)

interface MoreMenuItem {
	label: string
	onSelect: () => void
	/** "danger" renders the red sign-out styling. */
	tone?: "default" | "danger"
}

/**
 * The "More" bottom-nav item + its popover menu. Shared between the
 * global MobileBottomNav and the property-scoped PropertyBottomNav so
 * profile/sign-out stay reachable inside a property (Android parity).
 */
export const MobileMoreMenuItem: React.FC<{ items: MoreMenuItem[] }> = ({
	items,
}) => {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { moreOpen: open, setMoreOpen: setOpen } = useMobileMoreMenu()
	const moreRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return
		const handleDown = (e: MouseEvent) => {
			if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false)
		}
		window.addEventListener("mousedown", handleDown)
		window.addEventListener("keydown", handleKey)
		return () => {
			window.removeEventListener("mousedown", handleDown)
			window.removeEventListener("keydown", handleKey)
		}
	}, [open, setOpen])

	return (
		<div ref={moreRef} className="relative flex-1">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				aria-label={t.sidebar.more}
				aria-haspopup="menu"
				aria-expanded={open}
				className={
					"w-full h-full flex flex-col items-center justify-center gap-1 text-xs transition-colors " +
					(open
						? "text-foreground"
						: "text-muted-foreground hover:text-foreground")
				}
			>
				<MoreIcon />
				<span>{t.sidebar.more}</span>
			</button>
			{open ? (
				<div
					role="menu"
					className="absolute right-3 bottom-full mb-2 w-48 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-50 py-1"
				>
					<div className="flex items-center justify-between px-3 py-2 text-sm text-foreground/80">
						<span>{t.theme.toggleLabel}</span>
						<ThemePill />
					</div>
					<div className="my-1 border-t border-border" />
					{items.map((item) => (
						<button
							key={item.label}
							type="button"
							role="menuitem"
							onClick={() => {
								setOpen(false)
								item.onSelect()
							}}
							className={
								item.tone === "danger"
									? "w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
									: "w-full text-left px-3 py-2 text-sm text-foreground/80 hover:bg-accent hover:text-foreground"
							}
						>
							{item.label}
						</button>
					))}
				</div>
			) : null}
		</div>
	)
}
